import { Collapse, Switch } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Link } from 'react-router-dom'
import Section from '../common/Section'
import { Table } from 'ant-table-extensions'
import _ from 'lodash'
import { compareStrings } from '../../util/compareUtil'
import { defaultDateTime } from '../../util/dateTimeUtil'
import useQueryParams from '../../hooks/useQueryParams'
import useUsers from '../../hooks/api/useUsers'

const colProps = (dataIndex) => {
  return {
    sorter: (a, b) => compareStrings(_.get(a, dataIndex), _.get(b, dataIndex)),
  }
}

const UserListPage = () => {
  let { users: rawUsers, loading } = useUsers()
  const users = useMemo(
    () =>
      _.map(rawUsers, (u) => ({
        ...u,
        registrationNumber: u.registration?.registrationNumber,
        registrationLastname: u.registration?.lastName,
        membershipType: u.registration?.membershipType,
        username: _.startsWith(u.username, 'unset-') ? '(none)' : u.username,
      })),
    [rawUsers],
  )
  const [juniorLessonsOnly, setJuniorlessonsOnly] = useState(false)
  const [allData, setAllData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const query = useQueryParams()
  const initialSearch = query.get('search') || ''
  const tableWrapperRef = useRef(null)

  useEffect(() => {
    const data = juniorLessonsOnly ? _.filter(users, (u) => !_.isNil(u.juniorLessonsDay)) : users
    setAllData(data)
    const s = (initialSearch || '').trim().toLowerCase()
    if (!s) {
      setFilteredData(data)
    } else {
      setFilteredData(
        _.filter(data, (r) => {
          try {
            return JSON.stringify(r).toLowerCase().includes(s)
          } catch (e) {
            return false
          }
        }),
      )
    }
  }, [juniorLessonsOnly, users, initialSearch])

  // Prefill the built-in searchable input when a search param is provided
  useEffect(() => {
    if (!initialSearch) return
    const t = setTimeout(() => {
      try {
        const root = tableWrapperRef.current
        if (!root) return
        const input = root.querySelector('input')
        if (!input) return
        input.focus()
        input.value = initialSearch
        input.dispatchEvent(new Event('input', { bubbles: true }))
        input.dispatchEvent(new Event('change', { bubbles: true }))
      } catch (e) {
        // noop
      }
    }, 50)
    return () => clearTimeout(t)
  }, [initialSearch])

  const handleTableChange = (pagination, filters, sorter, extra) => setFilteredData(extra.currentDataSource)

  const columns = [
    { title: 'Row', width: '3em', dataIndex: 'id', render: (value, record, index) => <>{index + 1}</> },
    { title: 'ID', width: '3em', dataIndex: 'id', ...colProps('id') },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      width: '12em',
      ...colProps('createdAt'),
      render: (text, record) => defaultDateTime(text),
    },
    {
      title: 'Reg #',
      width: '10em',
      dataIndex: 'registrationNumber',
      ...colProps('registrationNumber'),
      render: (value) =>
        value ? <Link to={`/admin/registrations?search=${encodeURIComponent(value)}`}>{value}</Link> : '',
    },
    { title: 'Reg Lastname', width: '12em', dataIndex: 'registrationLastname', ...colProps('registrationLastname') },
    {
      title: 'Membership',
      width: '8em',
      dataIndex: 'membershipType',
      ...colProps('membershipType'),
      filters: _.uniq(_.map(users, 'membershipType')).map((type) => ({ text: type, value: type })),
      onFilter: (value, record) => record.membershipType === value,
    },
    { title: 'Last name', width: '15em', dataIndex: 'lastName', ...colProps('lastName') },
    { title: 'First name', width: '15em', dataIndex: 'firstName', ...colProps('firstName') },
    { title: 'Username', width: '20em', dataIndex: 'username', ...colProps('username') },
    { title: 'Age group', width: '7em', dataIndex: 'ageGroup', ...colProps('ageGroup') },
    { title: 'Gender', width: '7em', dataIndex: 'gender', ...colProps('gender') },
    { title: 'Email', width: '20em', dataIndex: 'email', ...colProps('email') },
    { title: 'Phone', width: '9em', dataIndex: 'phone', ...colProps('phone') },
    {
      title: 'Skill level',
      width: '5em',
      dataIndex: 'skill',
      ...colProps('skill'),
      filters: _.uniq(_.map(users, 'skill')).map((type) => ({ text: type, value: type })),
      onFilter: (value, record) => record.skill === value,
    },
    {
      title: 'Membership type',
      width: '9em',
      dataIndex: 'membershipType',
      ...colProps('membershipType'),
      filters: _.uniq(_.map(users, 'membershipType')).map((type) => ({ text: type, value: type })),
      onFilter: (value, record) => record.membershipType === value,
    },
    { title: 'Birth year', width: '6em', dataIndex: 'birthYear', ...colProps('birthYear') },
    {
      title: 'Public profile?',
      width: '7em',
      dataIndex: 'isProfilePublic',
      ...colProps('isProfilePublic'),
      render: (value) => (value ? 'YES' : 'NO'),
    },
    {
      title: 'Adult doubles?',
      width: '7em',
      dataIndex: 'interestedInAdultDoubles',
      ...colProps('interestedInAdultDoubles'),
      render: (value) => (value ? 'YES' : 'NO'),
      filters: [
        { text: 'YES', value: true },
        { text: 'NO', value: false },
      ],
      onFilter: (value, record) => record.interestedInAdultDoubles === value,
    },
    {
      title: 'Interurban?',
      width: '7em',
      dataIndex: 'interestedInInterurban',
      ...colProps('interestedInInterurban'),
      render: (value) => (value ? 'YES' : 'NO'),
      filters: [
        { text: 'YES', value: true },
        { text: 'NO', value: false },
      ],
      onFilter: (value, record) => record.interestedInInterurban === value,
    },
    {
      title: 'Junior lessons day',
      width: '9em',
      dataIndex: 'juniorLessonsDay',
      ...colProps('juniorLessonsDay'),
      filters: _.uniq(_.map(users, 'juniorLessonsDay')).map((type) => ({ text: type, value: type })),
      onFilter: (value, record) => record.juniorLessonsDay === value,
    },
    {
      title: 'Junior lessons time',
      width: '9em',
      dataIndex: 'juniorLessonsTime',
      ...colProps('juniorLessonsTime'),
      render: (text, record) => {
        if (text === '_4pm') {
          return '4:30'
        } else if (text === '_5pm') {
          return '5:30'
        } else {
          return text
        }
      },
      filters: _.uniq(_.map(users, 'juniorLessonsTime')).map((type) => ({
        text: type === '_4pm' ? '4:30' : type === '_5pm' ? '5:30' : type,
        value: type,
      })),
      onFilter: (value, record) => record.juniorLessonsTime === value,
    },
    {
      title: 'Disabled',
      width: '5em',
      dataIndex: 'blocked',
      ...colProps('blocked'),
      render: (value, record) => (value ? 'YES' : 'No'),
    },
  ]

  return (
    <Section title="Members">
      <b>Show junior lessons only?</b>{' '}
      <Switch checked={juniorLessonsOnly} onChange={() => setJuniorlessonsOnly((val) => !val)} />
      <div ref={tableWrapperRef}>
        <Table
          className="compactTable"
          size="small"
          columns={columns}
          loading={loading}
          dataSource={filteredData}
          pagination={false}
          exportable
          scroll={{ y: 500 }}
          exportableProps={{ dataSource: filteredData, showColumnPicker: true, btnProps: { type: 'link' } }}
          onChange={handleTableChange}
          searchable
        />
      </div>
    </Section>
  )
}

export default UserListPage
